/**
 * Media Manager Page
 * Filesystem-like interface for viewing and managing user uploaded files
 */

import { useState, useMemo, useRef, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  FormControl,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
  Breadcrumbs,
  Divider,
} from '@mui/material'
import {
  Search,
  GridView,
  ViewList,
  Image,
  VideoFile,
  Description,
  InsertDriveFile,
  Star,
  Delete,
  Info,
  Link,
  ArrowUpward,
  ArrowDownward,
  CloudUpload,
  Add,
  Home,
  CreateNewFolder,
  Folder,
  Edit,
  DriveFileMove,
} from '@mui/icons-material'
import { FileCategory, formatFileSize } from '@api/types/files'
import type { UserFileDto, MediaFolderDto, GetFilesParams } from '@api/types/files'
import {
  useFiles,
  useStorageQuota,
  useDeleteFile,
  useToggleFavorite,
  useUploadFile,
  useFolders,
  useDeleteFolder,
} from '../hooks/useFiles'
import { FileCard, FileCardSkeleton } from '../components/FileCard'
import { FolderCard, FolderCardSkeleton } from '../components/FolderCard'
import { FileListView } from '../components/FileListView'
import { FileDetailsDialog } from '../components/FileDetailsDialog'
import { FileUsagesDialog } from '../components/FileUsagesDialog'
import { CreateFolderDialog } from '../components/CreateFolderDialog'

type ViewMode = 'grid' | 'list'
type TabValue = 'all' | 'images' | 'videos' | 'documents' | 'favorites'
type SortBy = 'name' | 'date' | 'size'

const PAGE_SIZE = 24

export default function MediaManagerPage() {
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('date')
  const [sortDesc, setSortDesc] = useState(true)
  const [page, setPage] = useState(1)
  const [categoryFilter, setCategoryFilter] = useState<FileCategory | ''>('')

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [folderPath, setFolderPath] = useState<Array<{ id: string | null; name: string }>>([
    { id: null, name: 'My Files' },
  ])

  // Dialog state
  const [selectedFile, setSelectedFile] = useState<UserFileDto | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [usagesOpen, setUsagesOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<UserFileDto | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [createFolderOpen, setCreateFolderOpen] = useState(false)
  const [deleteFolderConfirmOpen, setDeleteFolderConfirmOpen] = useState(false)
  const [folderToDelete, setFolderToDelete] = useState<MediaFolderDto | null>(null)

  // Upload state
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
    file: UserFileDto
  } | null>(null)

  // Folder context menu state
  const [folderContextMenu, setFolderContextMenu] = useState<{
    mouseX: number
    mouseY: number
    folder: MediaFolderDto
  } | null>(null)

  // Build query params
  const queryParams = useMemo<GetFilesParams>(() => {
    const params: GetFilesParams = {
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortDesc,
    }

    // Only apply folder filter when not searching and on "all" tab
    if (!searchTerm && activeTab === 'all') {
      params.folderId = currentFolderId ?? undefined
    }

    if (searchTerm) params.searchTerm = searchTerm
    if (categoryFilter !== '') params.category = categoryFilter

    // Tab-based filters
    switch (activeTab) {
      case 'images':
        params.mimeTypePrefix = 'image/'
        break
      case 'videos':
        params.mimeTypePrefix = 'video/'
        break
      case 'documents':
        params.mimeTypePrefix = 'application/'
        break
      case 'favorites':
        params.favoritesOnly = true
        break
    }

    return params
  }, [page, sortBy, sortDesc, searchTerm, categoryFilter, activeTab, currentFolderId])

  // Data fetching
  const { data: filesData, isLoading: filesLoading, error: filesError } = useFiles(queryParams)
  const { data: allFolders, isLoading: foldersLoading } = useFolders()
  const { data: quota } = useStorageQuota()
  const deleteFile = useDeleteFile()
  const deleteFolder = useDeleteFolder()
  const toggleFavorite = useToggleFavorite()
  const uploadFile = useUploadFile()

  const rawFiles = filesData?.files ?? []
  const totalPages = filesData?.totalPages ?? 1

  // Filter files to only show those in the current folder
  // When at root (currentFolderId is null), only show files without a folderId
  // When in a subfolder, the API already filters by folderId
  const files = useMemo(() => {
    // Only filter when on "all" tab without search (folder browsing mode)
    if (activeTab === 'all' && !searchTerm && currentFolderId === null) {
      return rawFiles.filter((f) => !f.folderId)
    }
    return rawFiles
  }, [rawFiles, activeTab, searchTerm, currentFolderId])

  // Get folders in current directory
  const currentFolders = useMemo(() => {
    if (!allFolders) return []
    return allFolders.filter((f) => f.parentFolderId === currentFolderId)
  }, [allFolders, currentFolderId])

  // Upload handlers
  const handleFiles = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return

    const filesToUpload = Array.from(fileList)
    setUploadProgress({ current: 0, total: filesToUpload.length })

    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        await uploadFile.mutateAsync({
          file: filesToUpload[i],
          options: { folderId: currentFolderId ?? undefined },
        })
        setUploadProgress({ current: i + 1, total: filesToUpload.length })
      } catch (error) {
        console.error('Failed to upload file:', filesToUpload[i].name, error)
      }
    }

    // Reset after a short delay to show completion
    setTimeout(() => {
      setUploadProgress(null)
    }, 1000)
  }, [uploadFile, currentFolderId])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [handleFiles])

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  // File handlers
  const handleFileClick = (file: UserFileDto) => {
    setSelectedFile(file)
    setDetailsOpen(true)
  }

  const handleContextMenuOpen = (event: React.MouseEvent<HTMLElement>, file: UserFileDto) => {
    event.preventDefault()
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      file,
    })
  }

  const handleContextMenuClose = () => {
    setContextMenu(null)
  }

  const handleViewDetails = () => {
    if (contextMenu) {
      setSelectedFile(contextMenu.file)
      setDetailsOpen(true)
      handleContextMenuClose()
    }
  }

  const handleViewUsages = () => {
    if (contextMenu) {
      setSelectedFile(contextMenu.file)
      setUsagesOpen(true)
      handleContextMenuClose()
    }
  }

  const handleCopyUrl = async () => {
    if (contextMenu) {
      await navigator.clipboard.writeText(contextMenu.file.fileUrl)
      handleContextMenuClose()
    }
  }

  const handleToggleFavorite = (file: UserFileDto) => {
    toggleFavorite.mutate(file.id)
  }

  const handleDeleteClick = () => {
    if (contextMenu) {
      setFileToDelete(contextMenu.file)
      setDeleteConfirmOpen(true)
      handleContextMenuClose()
    }
  }

  const handleDeleteConfirm = async (force = false) => {
    if (fileToDelete) {
      try {
        await deleteFile.mutateAsync({ fileId: fileToDelete.id, force })
        setDeleteConfirmOpen(false)
        setFileToDelete(null)
        setDeleteError(null)
        if (selectedFile?.id === fileToDelete.id) {
          setDetailsOpen(false)
          setSelectedFile(null)
        }
      } catch (error: unknown) {
        // Check if the error is about the file being in use
        const errorMessage = error instanceof Error ? error.message : String(error)
        if (errorMessage.includes('still in use') || errorMessage.includes('force=true')) {
          setDeleteError(errorMessage)
        } else {
          setDeleteError('Failed to delete file. Please try again.')
        }
      }
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    setPage(1)
  }

  const toggleSortDirection = () => {
    setSortDesc(!sortDesc)
  }

  // Folder navigation handlers
  const handleFolderClick = (folder: MediaFolderDto) => {
    setCurrentFolderId(folder.id)
    setFolderPath((prev) => [...prev, { id: folder.id, name: folder.name || 'Unnamed' }])
    setPage(1)
  }

  const handleBreadcrumbClick = (index: number) => {
    const targetFolder = folderPath[index]
    setCurrentFolderId(targetFolder.id)
    setFolderPath((prev) => prev.slice(0, index + 1))
    setPage(1)
  }

  const handleFolderContextMenuOpen = (event: React.MouseEvent<HTMLElement>, folder: MediaFolderDto) => {
    event.preventDefault()
    setFolderContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      folder,
    })
  }

  const handleFolderContextMenuClose = () => {
    setFolderContextMenu(null)
  }

  const handleDeleteFolderClick = () => {
    if (folderContextMenu) {
      setFolderToDelete(folderContextMenu.folder)
      setDeleteFolderConfirmOpen(true)
      handleFolderContextMenuClose()
    }
  }

  const handleDeleteFolderConfirm = async () => {
    if (folderToDelete) {
      await deleteFolder.mutateAsync(folderToDelete.id)
      setDeleteFolderConfirmOpen(false)
      setFolderToDelete(null)
    }
  }

  const isUploading = uploadFile.isPending || uploadProgress !== null
  const showFolders = activeTab === 'all' && !searchTerm

  return (
    <Box>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileInputChange}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
      />

      {/* Upload Progress */}
      {uploadProgress && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" gutterBottom>
              Uploading files... ({uploadProgress.current} / {uploadProgress.total})
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(uploadProgress.current / uploadProgress.total) * 100}
            />
          </Box>
        </Alert>
      )}

      {/* Compact Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          {/* Upload button */}
          <Button
            variant="contained"
            size="small"
            startIcon={<Add fontSize="small" />}
            onClick={handleUploadClick}
            disabled={isUploading}
          >
            Upload
          </Button>

          {/* New Folder button */}
          {showFolders && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreateNewFolder fontSize="small" />}
              onClick={() => setCreateFolderOpen(true)}
            >
              New Folder
            </Button>
          )}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Search */}
          <TextField
            placeholder="Search..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: 150 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              sx: { height: 32 },
            }}
          />

          {/* Category filter */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={categoryFilter}
              displayEmpty
              onChange={(e) => {
                setCategoryFilter(e.target.value as FileCategory | '')
                setPage(1)
              }}
              sx={{ height: 32, fontSize: '0.875rem' }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={FileCategory.Personal}>Personal</MenuItem>
              <MenuItem value={FileCategory.Content}>Content</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          {/* Storage indicator */}
          {quota && (
            <Tooltip title={`${formatFileSize(quota.personalBytesUsed)} of ${formatFileSize(quota.personalBytesLimit)} used`}>
              <Chip
                size="small"
                label={`${Math.round(quota.personalUsagePercent)}%`}
                color={quota.personalUsagePercent > 90 ? 'error' : quota.personalUsagePercent > 70 ? 'warning' : 'default'}
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Tooltip>
          )}

          {/* View mode toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="grid" sx={{ p: 0.5 }}>
              <GridView fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" sx={{ p: 0.5 }}>
              <ViewList fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Breadcrumb Navigation - Only when in subfolders */}
        {showFolders && folderPath.length > 1 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Breadcrumbs sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}>
              {folderPath.map((item, index) => {
                const isLast = index === folderPath.length - 1
                return isLast ? (
                  <Typography key={item.id ?? 'root'} color="text.primary" fontWeight={600} variant="body2">
                    {item.name}
                  </Typography>
                ) : (
                  <Button
                    key={item.id ?? 'root'}
                    size="small"
                    startIcon={index === 0 ? <Home fontSize="small" /> : <Folder fontSize="small" />}
                    onClick={() => handleBreadcrumbClick(index)}
                    sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                  >
                    {item.name}
                  </Button>
                )
              })}
            </Breadcrumbs>
          </Box>
        )}

        {/* Active filters */}
        {(searchTerm || categoryFilter !== '') && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {searchTerm && (
              <Chip
                label={`Search: ${searchTerm}`}
                onDelete={() => setSearchTerm('')}
                size="small"
              />
            )}
            {categoryFilter !== '' && (
              <Chip
                label={`Category: ${FileCategory[categoryFilter]}`}
                onDelete={() => setCategoryFilter('')}
                size="small"
              />
            )}
          </Stack>
        )}
      </Paper>

      {/* Loading state */}
      {(filesLoading || foldersLoading) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
              xl: 'repeat(6, 1fr)',
            },
            gap: 2,
          }}
        >
          {showFolders && Array.from({ length: 4 }).map((_, i) => (
            <FolderCardSkeleton key={`folder-${i}`} />
          ))}
          {Array.from({ length: 8 }).map((_, i) => (
            <FileCardSkeleton key={`file-${i}`} />
          ))}
        </Box>
      )}

      {/* Error state */}
      {filesError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load files. Please try again later.
        </Alert>
      )}

      {/* Empty state - Drag & Drop Zone */}
      {!filesLoading && !foldersLoading && !filesError && files.length === 0 && currentFolders.length === 0 && (
        <Paper
          elevation={0}
          onClick={handleUploadClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'divider',
            bgcolor: isDragOver ? 'primary.light' : 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          <CloudUpload
            sx={{
              fontSize: 64,
              color: isDragOver ? 'primary.main' : 'text.secondary',
              mb: 2,
            }}
          />
          <Typography variant="h6" color={isDragOver ? 'primary.main' : 'text.secondary'} gutterBottom>
            {isDragOver ? 'Drop files here' : 'No Files Found'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm
              ? 'No files match your search criteria.'
              : activeTab === 'favorites'
                ? "You haven't marked any files as favorites yet."
                : 'Drag and drop files here, or click to browse'}
          </Typography>
          {!searchTerm && activeTab !== 'favorites' && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation()
                handleUploadClick()
              }}
              disabled={isUploading}
            >
              Select Files
            </Button>
          )}
        </Paper>
      )}

      {/* Folders and Files - Grid View */}
      {!filesLoading && !foldersLoading && !filesError && (currentFolders.length > 0 || files.length > 0) && viewMode === 'grid' && (
        <>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(6, 1fr)',
              },
              gap: 2,
              p: isDragOver ? 2 : 0,
              border: isDragOver ? '2px dashed' : 'none',
              borderColor: 'primary.main',
              borderRadius: 2,
              bgcolor: isDragOver ? 'primary.light' : 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            {/* Folders first */}
            {showFolders && currentFolders.map((folder) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                onClick={() => handleFolderClick(folder)}
                onMenuClick={(e) => handleFolderContextMenuOpen(e, folder)}
              />
            ))}

            {/* Then files */}
            {files.map((file) => (
              <FileCard
                key={file.id}
                file={file}
                onClick={() => handleFileClick(file)}
                onFavoriteToggle={() => handleToggleFavorite(file)}
                onMenuClick={(e) => handleContextMenuOpen(e, file)}
              />
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Folders and Files - List View */}
      {!filesLoading && !foldersLoading && !filesError && (currentFolders.length > 0 || files.length > 0) && viewMode === 'list' && (
        <>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              p: isDragOver ? 2 : 0,
              border: isDragOver ? '2px dashed' : 'none',
              borderColor: 'primary.main',
              borderRadius: 2,
              bgcolor: isDragOver ? 'primary.light' : 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            <FileListView
              folders={currentFolders}
              files={files}
              showFolders={showFolders}
              onFolderClick={handleFolderClick}
              onFolderMenuClick={handleFolderContextMenuOpen}
              onFileClick={handleFileClick}
              onFileFavoriteToggle={handleToggleFavorite}
              onFileMenuClick={handleContextMenuOpen}
            />
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Drag overlay when files exist */}
      {isDragOver && files.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6">Drop files to upload</Typography>
          </Paper>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <Info fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewUsages}>
          <ListItemIcon>
            <Link fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Usages</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyUrl}>
          <ListItemIcon>
            <Link fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* File Details Dialog */}
      <FileDetailsDialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false)
          setSelectedFile(null)
        }}
        file={selectedFile}
        onDelete={() => {
          setFileToDelete(selectedFile)
          setDeleteConfirmOpen(true)
        }}
      />

      {/* File Usages Dialog */}
      <FileUsagesDialog
        open={usagesOpen}
        onClose={() => {
          setUsagesOpen(false)
          setSelectedFile(null)
        }}
        fileId={selectedFile?.id || null}
        fileName={selectedFile?.displayName || selectedFile?.fileName}
      />

      {/* Delete File Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setFileToDelete(null)
          setDeleteError(null)
        }}
      >
        <DialogTitle>Delete File?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>
              {fileToDelete?.displayName || fileToDelete?.fileName}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false)
              setFileToDelete(null)
              setDeleteError(null)
            }}
          >
            Cancel
          </Button>
          {deleteError ? (
            <Button
              color="error"
              variant="contained"
              onClick={() => handleDeleteConfirm(true)}
              disabled={deleteFile.isPending}
            >
              {deleteFile.isPending ? <CircularProgress size={24} /> : 'Force Delete'}
            </Button>
          ) : (
            <Button
              color="error"
              onClick={() => handleDeleteConfirm(false)}
              disabled={deleteFile.isPending}
            >
              {deleteFile.isPending ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Folder Context Menu */}
      <Menu
        open={folderContextMenu !== null}
        onClose={handleFolderContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          folderContextMenu !== null
            ? { top: folderContextMenu.mouseY, left: folderContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => {
          if (folderContextMenu) {
            handleFolderClick(folderContextMenu.folder)
            handleFolderContextMenuClose()
          }
        }}>
          <ListItemIcon>
            <Folder fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open Folder</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteFolderClick}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Folder</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        parentFolderId={currentFolderId}
        folders={allFolders}
      />

      {/* Delete Folder Confirmation Dialog */}
      <Dialog
        open={deleteFolderConfirmOpen}
        onClose={() => {
          setDeleteFolderConfirmOpen(false)
          setFolderToDelete(null)
        }}
      >
        <DialogTitle>Delete Folder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the folder{' '}
            <strong>{folderToDelete?.name || 'Unnamed'}</strong>?
            {(folderToDelete?.fileCount ?? 0) > 0 && (
              <>
                {' '}This folder contains {folderToDelete?.fileCount} file
                {folderToDelete?.fileCount !== 1 ? 's' : ''}.
              </>
            )}
            {' '}This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteFolderConfirmOpen(false)
              setFolderToDelete(null)
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleDeleteFolderConfirm}
            disabled={deleteFolder.isPending}
          >
            {deleteFolder.isPending ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
